/**
 * 209's follow-up (the owner, playing 209 (5): "you can't get onto the pad as a player. I think we still can't walk on surfaces
 * of buildings ... Can't walk on them either"): the walkable tops a person stands on — a structure's deck read as ground.
 * One rule, for every reader of the person's ground (fineTerrain.ts personSurfaceRadius: the walk, the feet, the eye, a round
 * at foot height): over a stand's footprint the ground is its top where that is higher; round its edge a skirt `skirt` wide
 * ramps from the top down to the ground beyond (a step where the top stands within a step, a short ramp where it is higher),
 * so its edge is never a wall. The first is the landing pad's deck; lane F's building footings and plinths are next
 * (core/stands.ts STAND_TOPS). Pure; only the cell table imported, so the fine ground can read it without a cycle.
 * 209's footing follow-up (lane F): with `cells` (the stands filed by cell, core/stands.ts withStands) a query tests the
 * stands of the cells within the widest reach of the point — a handful — not every stand on the planet.
 */
import type { Stand, StandCells } from '../types';
import { queryCellIndexCount, cellQueryIds } from './cells';

const q = { x: 0, y: 0, z: 0 };
/** The person's ground at unit direction (ux, uy, uz) on a planet of `radius` with `stands`, given the ground there without them. */
export function standGround(stands: readonly Stand[], radius: number, ux: number, uy: number, uz: number, ground: number, cells?: StandCells): number {
  let r = ground;
  let n = stands.length, ids: Uint32Array | null = null;
  if (cells) { q.x = ux; q.y = uy; q.z = uz; n = queryCellIndexCount(cells.index, q, cells.angle, false); ids = cellQueryIds(); }
  for (let i = 0; i < n; i++) {
    const s = stands[ids ? ids[i] : i];
    if (ux * s.dir.x + uy * s.dir.y + uz * s.dir.z < s.cosReach) continue;
    // (in its frame: along the ground from its centre — the tangent components, for a footprint of a few people exact enough)
    const lx = (ux * s.tx.x + uy * s.tx.y + uz * s.tx.z) * radius, lz = (ux * s.tz.x + uy * s.tz.y + uz * s.tz.z) * radius;
    const ex = Math.max(0, Math.abs(lx) - s.halfX), ez = Math.max(0, Math.abs(lz) - s.halfZ);
    const e = ex === 0 ? ez : ez === 0 ? ex : Math.sqrt(ex * ex + ez * ez);
    if (e >= s.skirt && e > 0) continue;
    const h = e === 0 ? s.top : s.top + (ground - s.top) * (e / s.skirt);
    if (h > r) r = h;
  }
  return r;
}
